/**
 * Account Service
 * Real-time Bitfinex account information API
 */

import { api } from './api'

export interface Wallet {
  currency: string
  balance: number
  balanceAvailable: number | null
  unsettledInterest: number
}

export interface WalletsResponse {
  success: boolean
  data: {
    apiKeyLabel: string
    wallets: {
      funding: Wallet[]
      exchange: Wallet[]
      margin: Wallet[]
    }
    summary: {
      totalUSD: number
      fundingUSD: number
      exchangeUSD: number
      marginUSD: number
    }
    timestamp: number
  }
}

export interface Offer {
  id: number
  amount: number
  rate: number
  rateAPR: number
  period: number
  createdAt: number
  updatedAt: number
  status: string
}

export interface OffersResponse {
  success: boolean
  data: {
    apiKeyLabel: string
    currency: string
    offers: Offer[]
    summary: {
      count: number
      totalAmount: number
      averageRate: number
      averageAPR: number
    }
    timestamp: number
  }
}

export interface Loan {
  id: number
  amount: number
  rate: number
  rateAPR: number
  period: number
  openedAt: number
  status: string
  elapsedDays: number
  remainingDays: number
  accruedInterest: number
  estimatedTotalInterest: number
}

export interface LoansResponse {
  success: boolean
  data: {
    apiKeyLabel: string
    currency: string
    loans: Loan[]
    summary: {
      count: number
      totalAmount: number
      averageRate: number
      averageAPR: number
      totalAccruedInterest: number
      totalEstimatedInterest: number
      estimatedDailyEarnings: number
    }
    timestamp: number
  }
}

export interface AccountSummary {
  success: boolean
  data: {
    apiKeyLabel: string
    currency: string
    wallets: {
      funding: {
        balance: number
        available: number
        unsettledInterest: number
      } | null
      exchange: {
        balance: number
        available: number
      } | null
    }
    market: {
      frr: number
      frrAPR: number
      bestOfferRate: number
    }
    activity: {
      offers: {
        count: number
        totalAmount: number
        averageRate: number
        averageAPR: number
      }
      loans: {
        count: number
        totalAmount: number
        averageRate: number
        averageAPR: number
      }
    }
    summary: {
      totalBalance: number
      availableBalance: number
      totalLent: number
      utilizationRate: number
      estimatedDailyEarnings: number
      estimatedMonthlyEarnings: number
      estimatedAnnualEarnings: number
    }
    timestamp: number
  }
}

/**
 * Get real-time wallet balances
 */
export async function getWallets(apiKeyId: number): Promise<WalletsResponse> {
  return api.get<WalletsResponse>('/account/wallets', {
    api_key_id: apiKeyId.toString(),
  })
}

/**
 * Get active funding offers
 */
export async function getOffers(apiKeyId: number, currency: string): Promise<OffersResponse> {
  return api.get<OffersResponse>('/account/offers', {
    api_key_id: apiKeyId.toString(),
    currency: currency.toUpperCase(),
  })
}

/**
 * Get active funding loans (credits)
 */
export async function getLoans(apiKeyId: number, currency: string): Promise<LoansResponse> {
  return api.get<LoansResponse>('/account/loans', {
    api_key_id: apiKeyId.toString(),
    currency: currency.toUpperCase(),
  })
}

/**
 * Get comprehensive account summary
 */
export async function getAccountSummary(
  apiKeyId: number,
  currency: string
): Promise<AccountSummary> {
  return api.get<AccountSummary>('/account/summary', {
    api_key_id: apiKeyId.toString(),
    currency: currency.toUpperCase(),
  })
}
